import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppHeader from "@/components/feature/AppHeader";
import AppFooter from "@/components/feature/AppFooter";
import WorkflowPanel from "@/pages/admin/approvals/components/WorkflowPanel";
import WorkflowModal from "@/pages/admin/approvals/components/WorkflowModal";
import ApprovalRequestPanel from "@/pages/admin/approvals/components/ApprovalRequestPanel";
import WebhookPanel from "@/pages/admin/approvals/components/WebhookPanel";
import EndpointModal from "@/pages/admin/approvals/components/EndpointModal";
import { useApprovals, type ApprovalWorkflow, type WorkflowInput } from "@/pages/admin/approvals/hooks/useApprovals";
import { useWebhooks, type EndpointInput, type WebhookEndpoint } from "@/pages/admin/approvals/hooks/useWebhooks";

type Tab = "flow" | "webhook";

interface Notice {
  text: string;
  tone: "success" | "error";
}

interface PendingDelete {
  kind: "workflow" | "endpoint";
  id: number;
  name: string;
}

export default function ApprovalsPage() {
  const { t } = useTranslation();
  const approvals = useApprovals();
  const webhooks = useWebhooks();

  const [tab, setTab] = useState<Tab>("flow");
  const [notice, setNotice] = useState<Notice | null>(null);
  const [workflowModalOpen, setWorkflowModalOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState<ApprovalWorkflow | null>(null);
  const [endpointModalOpen, setEndpointModalOpen] = useState(false);
  const [editingEndpoint, setEditingEndpoint] = useState<WebhookEndpoint | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const [deleting, setDeleting] = useState(false);

  const notify = useCallback((text: string, tone: "success" | "error" = "success") => {
    setNotice({ text, tone });
    window.setTimeout(() => setNotice(null), 4000);
  }, []);

  const stats = [
    { label: t("approvals.statWorkflows"), value: String(approvals.workflows.length), icon: "ri-flow-chart", tone: "primary" },
    {
      label: t("approvals.statPending"),
      value: String(approvals.requests.filter((item) => item.status === "pending").length),
      icon: "ri-time-line",
      tone: "accent",
    },
    {
      label: t("approvals.statDone"),
      value: String(approvals.requests.filter((item) => item.status !== "pending").length),
      icon: "ri-checkbox-circle-line",
      tone: "secondary",
    },
    { label: t("approvals.statEndpoints"), value: String(webhooks.endpoints.length), icon: "ri-webhook-line", tone: "primary" },
  ];

  const toneClass: Record<string, string> = {
    primary: "bg-primary-100 text-primary-700",
    accent: "bg-accent-100 text-accent-700",
    secondary: "bg-secondary-100 text-secondary-900",
  };

  const error = approvals.error || webhooks.error;
  const loading = approvals.loading || webhooks.loading;

  const handleWorkflowSubmit = async (input: WorkflowInput) => {
    await approvals.saveWorkflow(input);
  };

  const handleEndpointSubmit = async (input: EndpointInput) => {
    await webhooks.saveEndpoint(input);
  };

  const handleToggleEndpoint = async (endpoint: WebhookEndpoint) => {
    try {
      await webhooks.setStatus(endpoint.id, endpoint.status === "active" ? "paused" : "active");
      notify(t("approvals.endpointStatusChanged"), "success");
    } catch (err) {
      notify(err instanceof Error ? err.message : t("approvals.actionFailed"), "error");
    }
  };

  const handleProbeResume = async (endpoint: WebhookEndpoint) => {
    try {
      const { result, replayed, replayCount, replaySuccess, degraded } = await webhooks.probeResume(endpoint.id);
      if (!result.ok) {
        notify(t("approvals.recoverPrecheckFail", { error: result.error ?? "" }), "error");
        return;
      }
      if (degraded) {
        notify(t("approvals.recoverPrecheckDegraded", { count: replayCount }), "error");
        return;
      }
      if (replayed) {
        const replayOk = replaySuccess >= replayCount;
        notify(
          replayOk
            ? t("approvals.recoverPrecheckReplayOk", { count: replayCount })
            : t("approvals.recoverPrecheckReplayFail", { count: replayCount, success: replaySuccess }),
          replayOk ? "success" : "error",
        );
      } else {
        notify(t("approvals.recoverPrecheckOk"), "success");
      }
    } catch (err) {
      notify(err instanceof Error ? err.message : t("approvals.actionFailed"), "error");
    }
  };

  const handleMarkFixed = async (endpoint: WebhookEndpoint) => {
    try {
      await webhooks.markFixed(endpoint.id);
      notify(t("approvals.markFixedDone"), "success");
    } catch (err) {
      notify(err instanceof Error ? err.message : t("approvals.actionFailed"), "error");
    }
  };

  const handleUndoMarkFixed = async (endpoint: WebhookEndpoint) => {
    try {
      await webhooks.undoMarkFixed(endpoint.id);
      notify(t("approvals.undoMarkFixedDone"), "success");
    } catch (err) {
      notify(err instanceof Error ? err.message : t("approvals.actionFailed"), "error");
    }
  };

  const handleTest = async (endpoint: WebhookEndpoint) => {
    try {
      const event = (endpoint.events[0] as "approval.completed") ?? "approval.completed";
      const result = await webhooks.test(endpoint.id, event);
      notify(
        result.ok ? t("approvals.testDone") : t("approvals.testFailed", { error: result.error ?? "" }),
        result.ok ? "success" : "error",
      );
    } catch (err) {
      notify(err instanceof Error ? err.message : t("approvals.actionFailed"), "error");
    }
  };

  const handleRetry = async (delivery: { id: number }) => {
    try {
      const result = await webhooks.retry(delivery.id);
      notify(
        result.ok ? t("approvals.retryDone") : t("approvals.testFailed", { error: result.error ?? "" }),
        result.ok ? "success" : "error",
      );
    } catch (err) {
      notify(err instanceof Error ? err.message : t("approvals.actionFailed"), "error");
    }
  };

  const handleProcessRetries = async () => {
    try {
      const result = await webhooks.processRetries();
      if (result.processed === 0) {
        notify(t("approvals.retriesNone"), "success");
      } else {
        notify(
          t("approvals.retriesProcessed", { processed: result.processed, succeeded: result.succeeded }),
          "success",
        );
      }
    } catch (err) {
      notify(err instanceof Error ? err.message : t("approvals.actionFailed"), "error");
    }
  };

  const handleCheckTimeouts = async () => {
    try {
      const result = await approvals.checkTimeouts();
      if (result.reminded === 0 && result.escalated === 0) {
        notify(t("approvals.timeoutNone"), "success");
      } else {
        notify(
          t("approvals.timeoutResult", { reminded: result.reminded, escalated: result.escalated }),
          "success",
        );
      }
      approvals.reload();
    } catch (err) {
      notify(err instanceof Error ? err.message : t("approvals.actionFailed"), "error");
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      if (pendingDelete.kind === "workflow") {
        await approvals.deleteWorkflow(pendingDelete.id);
        notify(t("approvals.workflowDeleted"), "success");
      } else {
        await webhooks.deleteEndpoint(pendingDelete.id);
        notify(t("approvals.endpointDeleted"), "success");
      }
      setPendingDelete(null);
    } catch (err) {
      notify(err instanceof Error ? err.message : t("approvals.actionFailed"), "error");
    } finally {
      setDeleting(false);
    }
  };

  const reload = () => {
    approvals.reload();
    webhooks.reload();
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <AppHeader />

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6 md:py-16">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="cursor-pointer transition-colors hover:text-foreground-950">
                {t("approvals.breadcrumbHome")}
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
              <Link to="/admin" className="cursor-pointer transition-colors hover:text-foreground-950">
                {t("approvals.breadcrumbAdmin")}
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
              <span className="text-foreground-800">{t("approvals.title")}</span>
            </nav>
            <div className="mt-4 flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                  <span className="flex h-3 w-3 items-center justify-center">
                    <i className="ri-flow-chart" />
                  </span>
                  {t("approvals.badge")}
                </span>
                <h1 className="mt-3 font-heading text-2xl font-semibold tracking-tight text-foreground-950 md:text-4xl">
                  {t("approvals.title")}
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                  {t("approvals.subtitle")}
                </p>
              </div>
              <Link
                to="/admin"
                className="inline-flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-100 md:w-auto"
              >
                <span className="flex h-4 w-4 items-center justify-center text-primary-700">
                  <i className="ri-arrow-left-line" />
                </span>
                {t("approvals.backToAdmin")}
              </Link>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-14">
          {error ? (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-16 text-center">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-error-warning-line text-xl" />
              </span>
              <p className="text-sm text-foreground-600">{error}</p>
              <button
                type="button"
                onClick={reload}
                className="cursor-pointer rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                {t("approvals.reload")}
              </button>
            </div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {stats.map((card) => (
                  <div key={card.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-foreground-600">{card.label}</span>
                      <span className={`flex h-8 w-8 items-center justify-center rounded-md ${toneClass[card.tone]}`}>
                        <i className={`${card.icon} text-base`} />
                      </span>
                    </div>
                    <p className="mt-2 font-heading text-2xl font-semibold text-foreground-950">
                      {loading ? "--" : card.value}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-8 mb-6 inline-flex rounded-full border border-background-300 bg-background-100 p-1">
                <button
                  type="button"
                  onClick={() => setTab("flow")}
                  className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    tab === "flow" ? "bg-background-50 text-foreground-950" : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-git-branch-line" />
                  </span>
                  {t("approvals.tabFlow")}
                </button>
                <button
                  type="button"
                  onClick={() => setTab("webhook")}
                  className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    tab === "webhook" ? "bg-background-50 text-foreground-950" : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-webhook-line" />
                  </span>
                  {t("approvals.tabWebhook")}
                </button>
              </div>

              {tab === "flow" ? (
                <div className="flex flex-col gap-5">
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3">
                    <p className="flex items-center gap-2 text-xs text-foreground-600">
                      <span className="flex h-5 w-5 items-center justify-center text-secondary-700">
                        <i className="ri-time-line" />
                      </span>
                      {t("approvals.timeoutHint")}
                    </p>
                    <button
                      type="button"
                      onClick={() => void handleCheckTimeouts()}
                      className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-xs font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-100"
                    >
                      <span className="flex h-3.5 w-3.5 items-center justify-center text-secondary-700">
                        <i className="ri-alarm-warning-line" />
                      </span>
                      {t("approvals.checkTimeouts")}
                    </button>
                  </div>
                  <WorkflowPanel
                    workflows={approvals.workflows}
                    loading={loading}
                    onCreate={() => {
                      setEditingWorkflow(null);
                      setWorkflowModalOpen(true);
                    }}
                    onEdit={(workflow) => {
                      setEditingWorkflow(workflow);
                      setWorkflowModalOpen(true);
                    }}
                    onDelete={(workflow) => setPendingDelete({ kind: "workflow", id: workflow.id, name: workflow.name })}
                  />
                  <ApprovalRequestPanel
                    requests={approvals.requests}
                    decisions={approvals.decisions}
                    applications={approvals.applications}
                    workflows={approvals.workflows}
                    loading={loading}
                    onStart={approvals.startRequest}
                    onDecide={approvals.decide}
                    notify={notify}
                  />
                </div>
              ) : (
                <WebhookPanel
                  endpoints={webhooks.endpoints}
                  deliveries={webhooks.deliveries}
                  events={webhooks.events}
                  loading={loading}
                  onCreate={() => {
                    setEditingEndpoint(null);
                    setEndpointModalOpen(true);
                  }}
                  onEdit={(endpoint) => {
                    setEditingEndpoint(endpoint);
                    setEndpointModalOpen(true);
                  }}
                  onToggleStatus={handleToggleEndpoint}
                  onProbeResume={handleProbeResume}
                  onMarkFixed={handleMarkFixed}
                  onUndoMarkFixed={handleUndoMarkFixed}
                  onDelete={(endpoint) => setPendingDelete({ kind: "endpoint", id: endpoint.id, name: endpoint.name })}
                  onTest={handleTest}
                  onRetry={handleRetry}
                  onProcessRetries={handleProcessRetries}
                />
              )}
            </>
          )}
        </section>
      </main>

      <WorkflowModal
        open={workflowModalOpen}
        initial={editingWorkflow}
        onClose={() => {
          setWorkflowModalOpen(false);
          setEditingWorkflow(null);
        }}
        onSubmit={handleWorkflowSubmit}
        notify={notify}
      />

      <EndpointModal
        open={endpointModalOpen}
        initial={editingEndpoint}
        onClose={() => {
          setEndpointModalOpen(false);
          setEditingEndpoint(null);
        }}
        onSubmit={handleEndpointSubmit}
        notify={notify}
      />

      {pendingDelete && (
        <div className="fixed inset-0 z-[75] flex items-center justify-center bg-foreground-950/40 px-4">
          <div className="w-full max-w-sm rounded-lg border border-background-200 bg-background-50 p-5 text-center">
            <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
              <i className="ri-delete-bin-line text-xl" />
            </span>
            <p className="mt-3 text-sm font-medium text-foreground-950">{pendingDelete.name}</p>
            <p className="mt-2 text-xs leading-relaxed text-foreground-600">
              {pendingDelete.kind === "workflow" ? t("approvals.confirmDeleteWorkflow") : t("approvals.confirmDeleteEndpoint")}
            </p>
            <div className="mt-5 flex justify-center gap-2">
              <button
                type="button"
                onClick={() => setPendingDelete(null)}
                className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
              >
                {t("approvals.cancel")}
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={() => void confirmDelete()}
                className="cursor-pointer whitespace-nowrap rounded-md bg-accent-600 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {t("approvals.delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {notice && (
        <div className="fixed bottom-6 left-1/2 z-[80] -translate-x-1/2 px-4">
          <div
            className={`lh-fade-up flex items-center gap-2.5 rounded-lg px-4 py-3 text-sm ${
              notice.tone === "success" ? "bg-primary-600 text-background-50" : "bg-accent-600 text-background-50"
            }`}
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className={notice.tone === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} />
            </span>
            {notice.text}
          </div>
        </div>
      )}

      <AppFooter />
    </div>
  );
}